import { ManufacturerProductPropertyOptionDetail } from '@deltek/specification-client/core/Models/product'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { arrayMove } from '@dnd-kit/sortable'
import {
    Backdrop,
    CircularProgress,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import React, { FC, useCallback, useContext, useState } from 'react'

import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'

import { useApolloClient, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GetIsManufacturerOptionDetailsRemovable } from 'bpm/api/graphQL/queries/product.queries'
import {
    ProductContext,
    ProductContextType,
} from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingDetailsPage'
import { stripDoubleSpaces } from 'bpm/shared/utils/inputUtils'
import clsx from 'clsx'
import DndProperties from '../../components/DndProperties'
import { ERROR_CANNOT_REMOVE_OPTION } from '../../constants'
import { PPL_ACTIONS, usePPLContext } from '../../PPLProvider'
import { insertToIndex } from '../nonMSReducer'
import { NonMSSubPropertyType } from '../nonMSTypes'

const {
    propertiesDivider,
    premiumPrimary,
    premiumSecondary,
    buttonPrimary,
    defaultLight,
    errorColor,
    boxBorder,
    productDetailsText,
} = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    subPropertyNameInput: {
        width: '100%',
        border: 0,
        marginLeft: theme.spacing(0.75),
        marginRight: theme.spacing(0.75),
        padding: theme.spacing(0.75),
        fontWeight: 500,
        backgroundColor: defaultLight,
        textOverflow: 'ellipsis',
        '&::placeholder': {
            color: productDetailsText,
            fontWeight: 500,
        },
        '&.error': {
            borderBottom: `1px solid ${errorColor}`,
            outline: 'none',
        },
        '&:focus': {
            outline: 'none',
            borderBottom: `1px solid ${boxBorder}`,
        },
    },
    optionGroupContainer: {
        display: 'flex',
        width: 'inherit',
        height: 'auto',
        flexWrap: 'wrap',
        alignItems: 'center',
        maxWidth: '100%',
    },
    addIcon: {
        width: pxToRem(12),
        height: pxToRem(12),
    },
    addPremiumPlusProperty: {
        alignSelf: 'center',
        marginBottom: theme.spacing(1),
        padding: pxToRem(5),
        gap: pxToRem(10),
        width: pxToRem(24),
        height: pxToRem(24),
        color: defaultLight,
        backgroundColor: premiumPrimary,
        border: `1px solid ${premiumPrimary}`,
        borderRadius: pxToRem(100),
        '&:hover': {
            backgroundColor: premiumSecondary,
            border: `1px solid ${premiumSecondary}`,
            cursor: 'pointer',
        },
    },
    addButton: {
        fontSize: theme.spacing(1.5),
        color: buttonPrimary,
        border: 0,
        backgroundColor: defaultLight,
        '&.disabled': {
            color: propertiesDivider,
        },
    },
    subPropertyNameInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
    errorLabel: {
        color: errorColor,
        fontSize: '10px',
        marginLeft: theme.spacing(1),
    },
    addPropertyHover: {
        width: 'unset',
    },
    prefixPostfix: {
        fontSize: pxToRem(13),
        lineHeight: '1.7',
        display: 'inline-block',
        maxWidth: '100%',
        wordWrap: 'break-word',
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(2),
        paddingRight: theme.spacing(1),
        verticalAlign: 'bottom',
    },
}))

interface OptionGroupsProps {
    propIndex: number
    index: number
    subProperty: NonMSSubPropertyType
    groupIndex: number
    propertyName: string
}

const NonMSOptionGroupSection: FC<OptionGroupsProps> = ({
    propIndex,
    index,
    subProperty,
    groupIndex,
    propertyName,
}) => {
    const classes = useStyles()

    const { dispatch } = usePPLContext()

    const { product } = useContext<Partial<ProductContextType>>(ProductContext)
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const client = useApolloClient()

    const [subPropertyOptions, setSubPropertyOptions] = useState<
        Array<Partial<ManufacturerProductPropertyOptionDetail>>
    >(subProperty.optionGroups[groupIndex].options ?? [])
    const [isRemoveLoading, setIsRemoveLoading] = useState(false)

    const [selectedOptions, setSelectedOptions] = useState<Array<string>>(
        subProperty.supportedOptionIds ?? []
    )

    const determineSelected = useCallback(
        (value, optionId) =>
            !!value && !!selectedOptions && selectedOptions.includes(optionId),
        [selectedOptions]
    )

    const handleDragEnd = useCallback(
        (active, over) => {
            if (!!active && !!over && active.id !== over.id) {
                setSubPropertyOptions(
                    (
                        subPropArray: Array<
                            Partial<ManufacturerProductPropertyOptionDetail>
                        >
                    ) => {
                        const oldItem = subPropArray.findIndex(
                            (
                                item: Partial<
                                    ManufacturerProductPropertyOptionDetail
                                >
                            ) => item.optionId === active.id
                        )

                        const newItem = subPropArray.findIndex(
                            (
                                item: Partial<
                                    ManufacturerProductPropertyOptionDetail
                                >
                            ) => item.optionId === over.id
                        )

                        const update = arrayMove(subPropArray, oldItem, newItem)

                        dispatch({
                            type: PPL_ACTIONS.sortSetOptions,
                            payload: {
                                propIndex,
                                subPropIndex: index,
                                subPropertyOptions: update,
                                selectedOptions,
                                optionGroupIndex: groupIndex,
                            },
                        })

                        return update
                    }
                )
            }
        },
        [selectedOptions, dispatch, index, propIndex]
    )

    const onOptionBlur = useCallback(
        (id: string, value: string) => {
            setSubPropertyOptions((subPropArray) => {
                const optionIndex = subPropArray.findIndex(
                    (option) => option.optionId === id
                )
                const update = insertToIndex(
                    subPropArray,
                    optionIndex,
                    {
                        ...subPropArray[optionIndex],
                        value: stripDoubleSpaces(value),
                    },
                    true
                )

                dispatch({
                    type: PPL_ACTIONS.sortSetOptions,
                    payload: {
                        propIndex,
                        subPropIndex: index,
                        subPropertyOptions: update,
                        selectedOptions,
                        optionGroupIndex: groupIndex,
                    },
                })

                return update
            })
        },
        [propIndex, index, selectedOptions, groupIndex, stripDoubleSpaces]
    )

    const onUOMBlur = useCallback(
        (property) => {
            setSubPropertyOptions((subPropArray) => {
                const optionIndex = subPropArray.findIndex(
                    (option) => option.optionId === property.optionId
                )
                const update = insertToIndex(
                    subPropArray,
                    optionIndex,
                    property,
                    true
                )

                dispatch({
                    type: PPL_ACTIONS.sortSetOptions,
                    payload: {
                        propIndex,
                        subPropIndex: index,
                        subPropertyOptions: update,
                        selectedOptions,
                        optionGroupIndex: groupIndex,
                    },
                })

                return update
            })
        },
        [propIndex, index, selectedOptions, groupIndex]
    )

    const handleRemoveClick = useCallback(
        async (optionId: string) => {
            const optionGroupId = subProperty.optionGroups[groupIndex].groupId
            setIsRemoveLoading(true)

            try {
                const {
                    data: {
                        getManufacturerOptionDetails: {
                            manufacturerOptionGroupDetails,
                        },
                    },
                } = await client.query({
                    query: GetIsManufacturerOptionDetailsRemovable,
                    variables: {
                        input: {
                            listingId: product?.listingId ?? '',
                            details: [
                                {
                                    propertyId: subProperty.propertyId,
                                    optionGroupId,
                                    optionId,
                                },
                            ],
                        },
                    },
                })

                if (!!manufacturerOptionGroupDetails[0].isDetailsRemovable) {
                    setSubPropertyOptions((subPropArray) => {
                        const update = subPropArray.filter(
                            (option) => option.optionId !== optionId
                        )

                        dispatch({
                            type: PPL_ACTIONS.sortSetOptions,
                            payload: {
                                propIndex,
                                subPropIndex: index,
                                subPropertyOptions: update,
                                selectedOptions,
                                optionGroupIndex: groupIndex,
                            },
                        })

                        return update
                    })
                    setIsRemoveLoading(false)
                } else {
                    throw new Error()
                }
            } catch (error) {
                showGlobalSnackbar({
                    variables: {
                        message: ERROR_CANNOT_REMOVE_OPTION,
                        variant: 'error',
                        id: 'RemoveOptionFailed',
                    },
                })
            } finally {
                setIsRemoveLoading(false)
            }
        },
        [selectedOptions, dispatch, index, propIndex]
    )

    const handleSetSelected = useCallback(
        (id: string) => {
            setSelectedOptions((selectedOpts) => {
                const update = selectedOpts.includes(id)
                    ? selectedOpts?.filter((d) => d !== id)
                    : [...selectedOpts, id]

                const optionIndex = subPropertyOptions.findIndex(
                    (option) => option.optionId === id
                )
                const option = subPropertyOptions[optionIndex]

                dispatch({
                    type: PPL_ACTIONS.sortSetOptions,
                    payload: {
                        propIndex,
                        subPropIndex: index,
                        subPropertyOptions: insertToIndex(
                            subPropertyOptions,
                            optionIndex,
                            { ...option, isSupported: !option.isSupported },
                            true
                        ),
                        selectedOptions: update,
                        optionGroupIndex: groupIndex,
                    },
                })

                return update
            })
        },
        [subPropertyOptions, dispatch, index, propIndex]
    )

    const noop = useCallback(() => {}, [])

    return (
        <span className={classes.optionGroupContainer}>
            <Typography
                component="span"
                className={clsx({
                    [classes.prefixPostfix]:
                        subProperty.optionGroups[groupIndex].prefix,
                })}
                id={`${subProperty.propertyId}_${groupIndex}`}
            >
                {subProperty.optionGroups[groupIndex].prefix}
            </Typography>
            <DndProperties
                propertyName={propertyName}
                subPropertyName={subProperty.subPropertyName}
                premiumOptions={subPropertyOptions}
                determineSelected={determineSelected}
                onDragStart={noop}
                onDragEnd={handleDragEnd}
                onRemoveClick={handleRemoveClick}
                onSelectOption={handleSetSelected}
                onOptionBlur={onOptionBlur}
                onUOMBlur={onUOMBlur}
            />
            <Typography
                component="span"
                className={clsx({
                    [classes.prefixPostfix]:
                        subProperty.optionGroups[groupIndex].postfix,
                })}
                id={`${subProperty.propertyId}_${groupIndex}`}
            >
                {subProperty.optionGroups[groupIndex].postfix}
            </Typography>

            {isRemoveLoading && (
                <Backdrop className={classes.backdrop} open={isRemoveLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </span>
    )
}

export default NonMSOptionGroupSection
